import React, { useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import './DepositModal.css';

interface Factura {
  id: number;
  total: number;
  estado: string;
  emitido: string;
  vencimiento: string;
  urlpdf: string;
}

interface ApiResponse {
  fecha: string;
  monto: string;
  numero_comprobante: string;
  banco_emisor: string;
  numero_cuenta_destino: string;
  nombre_destinatario: string;
  validacion_cuenta: string;
  driveFile: { id: string; url: string };
}

interface DepositModalProps {
  show: boolean;
  onClose: () => void;
  factura: Factura;
  clienteNombre: string;
  clienteEstado: string;
}

interface AlertBoxProps {
  type: 'success' | 'danger';
  children: ReactNode;
}

const AlertBox: React.FC<AlertBoxProps> = ({ type, children }) => (
  <div className={`alert alert-${type} mt-2`} role="alert">
    {children}
  </div>
);

interface SupportButtonProps {
  nombre: string;
  driveUrl: string;
}
const SupportButton: React.FC<SupportButtonProps> = ({ nombre, driveUrl }) => {
  const phone = '593986031015';
  const message = encodeURIComponent(
    `Hola, soy ${nombre}. Mi comprobante no fue válido. Por favor revisa mi transacción y comprobante: ${driveUrl}`
  );
  const url = `https://wa.me/${phone}?text=${message}`;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="btn btn-success btn-sm mt-2"
      style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
        <path d="M13.601 2.326A7.95 7.95 0 0 0 8.036.166a7.952 7.952 0 0 0-5.63 2.159A7.99 7.99 0 0 0 .166 8a7.99 7.99 0 0 0 2.239 5.635l-.667 2.447 2.507-.667A7.987 7.987 0 0 0 8 15.834a7.952 7.952 0 0 0 5.635-2.158 7.99 7.99 0 0 0 2.159-5.63 7.952 7.952 0 0 0-2.192-5.72zm-5.565 11.239a6.475 6.475 0 0 1-3.306-.9l-.237-.14-1.486.395.396-1.45-.153-.234a6.47 6.47 0 0 1-1.03-3.283c0-3.592 2.922-6.514 6.514-6.514 1.74 0 3.373.68 4.59 1.91a6.47 6.47 0 0 1 1.91 4.593 6.475 6.475 0 0 1-6.474 6.474zm3.68-4.64c-.2-.1-1.178-.58-1.36-.64-.182-.058-.316-.1-.45.1-.132.201-.51.64-.625.77-.114.13-.228.145-.428.048-.2-.099-.846-.312-1.61-.993-.595-.533-.996-1.19-1.114-1.39-.117-.201-.012-.31.087-.41.089-.088.2-.228.3-.343.1-.114.133-.2.2-.333.066-.132.033-.247-.016-.347-.048-.099-.45-1.084-.616-1.488-.162-.39-.326-.337-.45-.343-.117-.007-.252-.008-.385-.008-.132 0-.347.049-.528.233-.182.186-.694.678-.694 1.654s.71 1.918.808 2.05c.099.132 1.395 2.13 3.375 2.987.472.203.84.324 1.126.415.473.155.905.133 1.247.081.38-.06 1.178-.482 1.345-.948.166-.466.166-.864.116-.948-.048-.083-.182-.132-.383-.232z"/>
      </svg>
      Soporte WhatsApp
    </a>
  );
};

const MAX_FILE_SIZE = 5 * 1024 * 1024;

// Convierte base64 dataUrl a File
const dataUrlToFile = (dataUrl: string, filename: string): File => {
  const [header, data] = dataUrl.split(',');
  const mime = header.match(/:(.*?);/)?.[1] ?? 'image/jpeg';
  const binary = atob(data);
  const array = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) array[i] = binary.charCodeAt(i);
  return new File([array], filename, { type: mime });
};

const DepositModal: React.FC<DepositModalProps> = ({ show, onClose, factura, clienteNombre, clienteEstado }) => {
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPdf, setIsPdf] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [responseData, setResponseData] = useState<ApiResponse | null>(null);
  const [paying, setPaying] = useState(false);

  const chatApiUrl = process.env.REACT_APP_CHATGPT_API_URL!;
  const paidInvoiceUrl = process.env.REACT_APP_N8N_URL_DEPOSITO_PAGAR!;

  useEffect(() => {
    if (!show) {
      setFile(null);
      setPreviewUrl(null);
      setIsPdf(false);
      setUploading(false);
      setError(null);
      setResponseData(null);
      setIsDuplicate(false);
      setPaying(false);
    }
  }, [show]);

  useEffect(() => {
    if (file && !isPdf) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    if (!file) setPreviewUrl(null);
  }, [file, isPdf]);

  if (!show) return null;

  const handleTakePhoto = async () => {
    setError(null);
    try {
      const photo = await Camera.getPhoto({
        quality: 80,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Camera,
        correctOrientation: true,
      });
      if (photo.dataUrl) {
        const f = dataUrlToFile(photo.dataUrl, 'foto_comprobante.jpg');
        setFile(f);
        setPreviewUrl(photo.dataUrl);
        setIsPdf(false);
        setResponseData(null);
        setIsDuplicate(false);
      }
    } catch {
      setError('No se pudo acceder a la cámara.');
    }
  };

  const handleGallery = async () => {
    setError(null);
    try {
      const photo = await Camera.getPhoto({
        quality: 80,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Photos,
        correctOrientation: true,
      });
      if (photo.dataUrl) {
        const f = dataUrlToFile(photo.dataUrl, 'comprobante_galeria.jpg');
        setFile(f);
        setPreviewUrl(photo.dataUrl);
        setIsPdf(false);
        setResponseData(null);
        setIsDuplicate(false);
      }
    } catch {
      setError('No se pudo abrir la galería.');
    }
  };

  const handlePdfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setResponseData(null);
    setIsDuplicate(false);
    const selected = e.target.files?.[0] || null;
    if (selected) {
      if (selected.type !== 'application/pdf') {
        setError('Solo se permiten archivos PDF en esta opción.');
        return;
      }
      if (selected.size > MAX_FILE_SIZE) {
        setError('El archivo supera el tamaño máximo de 5MB.');
        return;
      }
      setFile(selected);
      setPreviewUrl(null);
      setIsPdf(true);
    }
  };

  const handleUpload = async () => {
    if (!file) { setError('Selecciona o toma una foto primero.'); return; }
    setUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const resp = await axios.post<ApiResponse>(chatApiUrl, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResponseData(resp.data);
    } catch {
      setError('Error al subir. Intenta nuevamente.');
    } finally {
      setUploading(false);
    }
  };

  const handlePay = async () => {
    if (!responseData) { setError('No hay comprobante validado para pagar.'); return; }
    setPaying(true);
    setError(null);
    try {
      const payload = {
        idfactura: factura.id,
        pasarela: responseData.banco_emisor,
        idtransaccion: responseData.numero_comprobante,
        cantidad: parseFloat(responseData.monto),
        nota: `${responseData.driveFile.url} - fue pagado por la aplicacion web`,
      };
      const resp = await axios.post(paidInvoiceUrl, payload, { headers: { 'Content-Type': 'application/json' } });
      if (resp.data.estado === 'exito') {
        onClose();
        window.location.reload();
      } else {
        const message = resp.data.salida ?? 'Ese comprobante ya fue procesado en otro pago. Contacta soporte.';
        setError(`Error al registrar pago: ${message}`);
        if (!resp.data.salida) setIsDuplicate(true);
      }
    } catch {
      setError('Error al procesar pago. Intenta nuevamente.');
    } finally {
      setPaying(false);
    }
  };

  const renderMontoMessage = () => {
    if (!responseData) return null;
    const montoNum = parseFloat(responseData.monto);
    const diff = montoNum - factura.total;
    if (montoNum === factura.total) {
      return <AlertBox type="success">El monto coincide con la factura.</AlertBox>;
    }
    if (diff > 0 && diff <= 20) {
      return <AlertBox type="success">Saldo a favor de {diff.toFixed(2)} USD.</AlertBox>;
    }
    return (
      <>
        <AlertBox type="danger">{diff > 0 ? `Exceso de ${diff.toFixed(2)} USD.` : 'Monto insuficiente.'}</AlertBox>
        <SupportButton nombre={clienteNombre} driveUrl={responseData.driveFile.url} />
      </>
    );
  };

  const showPayButton = () => {
    if (isDuplicate) return false;
    if (!responseData || responseData.validacion_cuenta !== 'validado') return false;
    const montoNum = parseFloat(responseData.monto);
    const diff = montoNum - factura.total;
    return diff >= 0 && diff <= 20;
  };

  return (
    <>
      <div className="modal-backdrop fade show" />
      <div className="modal d-block" tabIndex={-1} role="dialog" aria-modal="true">
        <div className="modal-dialog" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Subir Comprobante de Depósito</h5>
              <button type="button" className="btn-close" aria-label="Cerrar" onClick={onClose} disabled={uploading || paying} />
            </div>

            <div className="modal-body">
              {/* Cliente */}
              <div className="d-flex align-items-center mb-2">
                <span className={`badge ${clienteEstado.toUpperCase() === 'ACTIVO' ? 'bg-success' : 'bg-danger'}`} style={{ minWidth: 75 }}>
                  {clienteEstado}
                </span>
                <strong className="ms-2">{clienteNombre}</strong>
              </div>

              {/* Factura */}
              <h6 className="mb-2">Factura #{factura.id}</h6>
              <table className="table table-sm table-bordered text-center mb-3">
                <tbody>
                  <tr>
                    <td>Emitido</td>
                    <td>Vencimiento</td>
                    <td>Total</td>
                    <td>Estado</td>
                  </tr>
                  <tr>
                    <td>{factura.emitido}</td>
                    <td>{factura.vencimiento}</td>
                    <td>${factura.total.toFixed(2)}</td>
                    <td>{factura.estado}</td>
                  </tr>
                </tbody>
              </table>

              {/* Validación y monto */}
              {responseData && (
                <>
                  <AlertBox type={responseData.validacion_cuenta === 'validado' ? 'success' : 'danger'}>
                    {responseData.validacion_cuenta === 'validado' ? 'Comprobante válido.' : 'Comprobante no válido.'}
                  </AlertBox>
                  {renderMontoMessage()}
                  <ul className="list-group mb-3 mt-2">
                    <li className="list-group-item"><strong>Fecha:</strong> {responseData.fecha}</li>
                    <li className="list-group-item"><strong>Monto:</strong> {responseData.monto}</li>
                    <li className="list-group-item"><strong>Nº comprobante:</strong> {responseData.numero_comprobante}</li>
                    <li className="list-group-item"><strong>Banco:</strong> {responseData.banco_emisor}</li>
                    <li className="list-group-item"><strong>Cuenta:</strong> {responseData.numero_cuenta_destino}</li>
                    <li className="list-group-item"><strong>Destinatario:</strong> {responseData.nombre_destinatario}</li>
                  </ul>
                </>
              )}

              {/* Selector de comprobante */}
              {!responseData && (
                <>
                  {/* Preview compacto */}
                  {previewUrl && !isPdf && (
                    <div className="deposit-preview-wrap mb-2">
                      <img src={previewUrl} alt="preview" className="deposit-preview-img" />
                      <span className="deposit-preview-label">Vista previa</span>
                    </div>
                  )}
                  {isPdf && file && (
                    <div className="deposit-pdf-badge mb-2">
                      📄 <strong>{file.name}</strong>
                    </div>
                  )}

                  {/* Botones de origen */}
                  <p className="text-muted small mb-2">Selecciona cómo subir el comprobante:</p>
                  <div className="deposit-btn-group mb-3">
                    <button className="deposit-source-btn" onClick={handleTakePhoto} disabled={uploading}>
                      <span className="deposit-source-icon">📷</span>
                      <span>Tomar<br />foto</span>
                    </button>
                    <button className="deposit-source-btn" onClick={handleGallery} disabled={uploading}>
                      <span className="deposit-source-icon">🖼️</span>
                      <span>Galería</span>
                    </button>
                    <label className={`deposit-source-btn ${uploading ? 'disabled' : ''}`}>
                      <span className="deposit-source-icon">📄</span>
                      <span>PDF</span>
                      <input
                        type="file"
                        accept="application/pdf"
                        style={{ display: 'none' }}
                        onChange={handlePdfChange}
                        disabled={uploading}
                      />
                    </label>
                  </div>

                  {/* Botón subir */}
                  {file && (
                    <button
                      type="button"
                      className="btn btn-primary w-100"
                      onClick={handleUpload}
                      disabled={uploading}
                    >
                      {uploading ? 'Analizando comprobante…' : '✔ Siguiente'}
                    </button>
                  )}
                </>
              )}

              {/* Spinner */}
              {(uploading || paying) && (
                <div className="text-center my-3">
                  <div className="spinner-border" role="status"><span className="visually-hidden">Procesando...</span></div>
                  <p className="mt-2 small">Espere...</p>
                </div>
              )}

              {error && <AlertBox type="danger">{error}</AlertBox>}
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={uploading || paying}>
                Cancelar
              </button>
              {showPayButton() && (
                <button type="button" className="btn btn-success ms-2" onClick={handlePay} disabled={paying}>
                  {paying ? 'Procesando…' : '💳 Pagar'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DepositModal;
